import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

let toastId = 0;

export function GravityToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info') => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useMemo(() => ({
    success: (msg) => addToast(msg, 'success'),
    error: (msg) => addToast(msg, 'error'),
    info: (msg) => addToast(msg, 'info'),
  }), [addToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9998] flex flex-col gap-3 items-center pointer-events-none" data-testid="gravity-toast-container">
        <AnimatePresence>
          {toasts.map(t => (
            <motion.div
              key={t.id}
              initial={{ y: 40, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="pointer-events-auto relative"
            >
              <div className="absolute inset-0 rounded-full animate-ripple" style={{
                border: `1px solid ${t.type === 'error' ? 'rgba(160,66,44,0.3)' : 'rgba(166,124,0,0.3)'}`,
                borderRadius: '9999px',
              }} />
              <div
                data-testid={`gravity-toast-${t.type}`}
                className="flex items-center gap-3 px-5 py-3 rounded-full backdrop-blur-xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.75)',
                  borderLeft: `3px solid ${t.type === 'success' ? '#A67C00' : t.type === 'error' ? '#A0422C' : '#6B5744'}`,
                  boxShadow: `0 8px 32px ${t.type === 'error' ? 'rgba(160,66,44,0.1)' : 'rgba(139,105,20,0.1)'}`,
                }}
              >
                {t.type === 'success' && <Check size={16} style={{ color: '#A67C00' }} />}
                {t.type === 'error' && <AlertTriangle size={16} style={{ color: '#A0422C' }} />}
                {t.type === 'info' && <Info size={16} style={{ color: '#6B5744' }} />}
                <span className="text-sm font-body" style={{ color: '#2C1810' }}>{t.message}</span>
                <button onClick={() => removeToast(t.id)} className="ml-2" style={{ color: '#9C8B7A' }}>
                  <X size={14} />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useGravityToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useGravityToast must be inside GravityToastProvider');
  return ctx;
}
